import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { CodeLabelSize, generateCodeLabelsPdf } from '@shared/utils/codeLabelPdf';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCarts } from '../../hooks/useCarts';
import { Cart } from '../../models/Cart';
import { ErrorState, LoadingState } from '../feedback';
import { CartsHeader, CartsModals, CartsView } from './';

type LabelSize = CodeLabelSize;

const createHeaderActions = (handleCreateCart: () => void, t: (key: string) => string) => [
  {
    id: 'create',
    label: t('button.create'),
    onClick: handleCreateCart,
    variant: 'primary' as const,
  },
];

const Main = () => {
  const { t } = useTranslation('cartsModule');
  const modalsRef = useRef<any>(null);

  // 🎯 Hook para manejar query parameters (para URL sync)
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );

  // Configure breadcrumbs with translated label
  const breadcrumbs = useMemo(() => [{ label: t('title'), path: '/carts' }], [t]);

  // 🎯 Sincronizar estados locales con cambios en URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  // Build advanced_filters payload from search term + filter conditions
  const processedFilters = useMemo(() => {
    const filters: any = {};
    const allFilters: any[] = [];

    if (searchTerm.trim()) {
      allFilters.push({
        column: 'code',
        operator: 'contains',
        value: searchTerm.trim(),
      });
    }

    if (filterConditions.length > 0) {
      allFilters.push(
        ...filterConditions.map((condition) => ({
          column: condition.column,
          operator: condition.action,
          value: condition.value,
        })),
      );
    }

    if (allFilters.length > 0) {
      filters.advanced_filters = {
        filters: allFilters,
        logic: 'AND' as const,
      };
    }

    return filters;
  }, [filterConditions, searchTerm]);

  const { carts, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, total } =
    useCarts({
      ...processedFilters, // searchTerm already included through advanced_filters
    });

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleCreateCart = useCallback(() => {
    modalsRef.current?.openCreateModal();
  }, []);

  const handleCartEdit = useCallback((cart: Cart) => {
    modalsRef.current?.openEditModal(cart);
  }, []);

  const handleCartDelete = useCallback((cart: Cart) => {
    modalsRef.current?.openDeleteModal(cart);
  }, []);

  const handleCartAdjust = useCallback((cart: Cart) => {
    modalsRef.current?.openAdjustModal(cart);
  }, []);

  const handleCartPrint = useCallback(async (cart: Cart, size: LabelSize) => {
    try {
      await generateCodeLabelsPdf({
        labels: [cart],
        labelSize: size,
        fileName: `etiqueta_carrito_${cart.code}_${Date.now()}.pdf`,
      });
    } catch (error) {
      console.error('Error generando PDF para carrito:', error);
    }
  }, []);

  const handleCartToggleLock = useCallback((cart: Cart) => {
    if (cart.is_locked) {
      modalsRef.current?.openUnlockModal(cart);
    } else {
      modalsRef.current?.openLockModal(cart);
    }
  }, []);

  const handleCartViewProducts = useCallback((cart: Cart) => {
    modalsRef.current?.openProductsModal(cart);
  }, []);

  // 🎯 Handlers para sincronizar con URL - memoizados para evitar bucles
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      // Solo actualizar URL si el valor es diferente al actual
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      // Solo actualizar URL si los filtros son diferentes
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const hasActiveFilters = filterConditions.length > 0;

  const headerActions = useMemo(
    () => (total > 0 ? createHeaderActions(handleCreateCart, t) : []),
    [handleCreateCart, t, total],
  );

  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error as Error} onRetry={handleRefetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header - filters & search */}
      <CartsHeader onSearch={handleSearch} onFilterChange={handleFilterChange} total={total} />

      {/* View - table/cards with infinite scroll */}
      <CartsView
        carts={carts}
        error={error}
        onCartEdit={handleCartEdit}
        onCartDelete={handleCartDelete}
        onCartAdjust={handleCartAdjust}
        onCartPrint={handleCartPrint}
        onCartToggleLock={handleCartToggleLock}
        onCartViewProducts={handleCartViewProducts}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={null}
        onSortingChange={() => {}}
        total={total}
        isLoading={isLoading}
        isFetching={isFetching}
        hasActiveFilters={hasActiveFilters}
        hasSearchTerm={Boolean(searchTerm)}
        onCreateCart={handleCreateCart}
      />

      {/* Modals - centralized management */}
      <CartsModals ref={modalsRef} />
    </div>
  );
};

export { Main };
